import React, { useMemo } from 'react';
import { IToolContext, DiagramType, SelectionSummary, ContextType, SelectionStylesAggregate, SelectionDataAggregate } from '../core/tools/interfaces/ITool';
import { getShapeManifest } from '../lib/diagram/registries';
import { BaseShape } from '../core/shapes/BaseShape';
import { BaseConnector } from '../core/connectors/BaseConnector';
import { IFrame } from '../core/frames/IFrame';
import { IEventBus } from '../core/events/IEventBus';
import { useDiagramStore } from '../stores/diagramStore';
import Konva from 'konva';

const MANIFEST_SHAPE_TYPE_PREFIX = /^(aws|azure|gcp|k8s|generic|basic)_/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasStringValue = (values: unknown[]) =>
  values.some((value) => typeof value === 'string');

interface UseToolContextProps {
  stageRef: React.RefObject<Konva.Stage>;
  layerRef: React.RefObject<Konva.Layer>;
  eventBus: IEventBus;
  selectedIds: Set<string>;
  currentFrame?: IFrame | null;
  diagramType?: DiagramType;
}

export const useToolContext = ({
  stageRef,
  layerRef,
  eventBus,
  selectedIds,
  currentFrame = null,
  diagramType = 'genogram'
}: UseToolContextProps): IToolContext => {
  // Only subscribe to nodes and edges, not the entire store
  // This prevents re-renders when unrelated state changes
  const nodes = useDiagramStore(state => state.nodes);
  const edges = useDiagramStore(state => state.edges);
  
  // Convert nodes to shapes map
  const shapesMap = useMemo(() => {
    const map = new Map<string, BaseShape<any>>();
    nodes.forEach(node => {
      // Create a mock BaseShape from node data
      const shape = {
        id: node.id,
        type: node.type,
        // Many tools read attrs directly (e.g., diagram linking).
        attrs: (node as any).attrs || {},
        data: node,
        // Add other BaseShape properties as needed
        getBounds: () => ({
          x: node.x,
          y: node.y,
          width: node.w || 120,
          height: node.h || 60
        }),
        // Mock other required methods
        render: () => null,
        update: () => {},
        destroy: () => {}
      } as unknown as BaseShape<any>;
      
      map.set(node.id, shape);
    });
    return map;
  }, [nodes]);

  // Convert edges to connectors map
  const connectorsMap = useMemo(() => {
    const map = new Map<string, BaseConnector<any>>();
    edges.forEach(edge => {
      // Create a mock BaseConnector from edge data
      const connector = {
        id: edge.id,
        type: edge.type,
        from: edge.from,
        to: edge.to,
        data: edge,
        // Mock other required methods
        render: () => null,
        update: () => {},
        destroy: () => {}
      } as unknown as BaseConnector<any>;
      
      map.set(edge.id, connector);
    });
    return map;
  }, [edges]);

  // Helper methods for the context
  const getSelectedShapes = () => {
    return Array.from(selectedIds)
      .map(id => shapesMap.get(id))
      .filter(Boolean) as BaseShape<any>[];
  };

  const getSelectedConnectors = () => {
    return Array.from(selectedIds)
      .map(id => connectorsMap.get(id))
      .filter(Boolean) as BaseConnector<any>[];
  };

  const getShapesInFrame = (_frameId: string) => {
    // TODO: Implement frame-based filtering when frames are fully implemented
    return Array.from(shapesMap.values());
  };

  // Compute lightweight selection summary for emergent UI
  const selectionSummary = useMemo<SelectionSummary>(() => {
    const typesSet = new Set<ContextType>();
    const countsByType: Record<ContextType, number> = {
      none: 0, text: 0, person: 0, genericShape: 0, connector: 0, frame: 0, 'sticky-note': 0, image: 0, pdf: 0, mixed: 0
    };
    let hasText = false;
    const ids = Array.from(selectedIds);
    ids.forEach((id) => {
      const shape = nodes.find(n => n.id === id);
      if (shape) {
        let t: ContextType = 'genericShape';
        if (shape.type === 'text') t = 'text';
        else if (shape.type === 'person') t = 'person';
        else if (shape.type === 'sticky-note') t = 'sticky-note';
        else if (shape.type === 'image') t = 'image';
        else if (shape.type === 'pdf') t = 'pdf';
        typesSet.add(t);
        countsByType[t] = (countsByType[t] || 0) + 1;
        const shapeRecord = shape as unknown as Record<string, unknown>;
        const attrs = isRecord(shapeRecord.attrs) ? shapeRecord.attrs : {};
        const props = isRecord(shapeRecord.props) ? shapeRecord.props : {};
        const propsAttrs = isRecord(props.attrs) ? props.attrs : {};
        const isManifestLikeNode =
          typeof attrs._manifestId === 'string' ||
          typeof attrs.manifestId === 'string' ||
          typeof shapeRecord._manifestId === 'string' ||
          typeof shapeRecord.manifestId === 'string' ||
          MANIFEST_SHAPE_TYPE_PREFIX.test(String(shape.type ?? ''));
        const hasLabelText = hasStringValue([
          shapeRecord.text,
          shapeRecord.name,
          attrs.text,
          attrs.name,
          props.text,
          props.name,
          propsAttrs.text,
          propsAttrs.name,
        ]);
        // Determine text capability: text nodes, sticky notes, or manifests with textEditable
        if (
          shape.type === 'text' ||
          shape.type === 'sticky-note' ||
          shape.type === 'basic-shape' ||
          shape.type === 'flowchart-shape' ||
          shape.type === 'bpmn-shape' ||
          shape.type === 'org-shape'
        ) {
          hasText = true;
        } else if (hasLabelText || isManifestLikeNode) {
          hasText = true;
        } else {
          try {
            const manifest = getShapeManifest(diagramType as any, shape.type as unknown as string);
            if (manifest?.capabilities?.textEditable) hasText = true;
          } catch {}
        }
        return;
      }
      const edge = edges.find(e => e.id === id);
      if (edge) {
        typesSet.add('connector');
        countsByType.connector = (countsByType.connector || 0) + 1;
      }
    });
    const ctxType: ContextType = typesSet.size === 0
      ? 'none'
      : typesSet.size === 1
        ? Array.from(typesSet)[0]
        : 'mixed';

    // Create a stable version number based on selection size and context type
    // Only changes when the actual selection changes, not on every render
    const versionKey = `${ids.length}-${ctxType}-${hasText}`;

    return {
      contextType: ctxType,
      typesSet,
      countsByType,
      diagramType,
      flags: {
        hasText,
        hasConnectors: countsByType.connector > 0,
      },
      version: versionKey.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0),
    };
  }, [selectedIds, nodes, edges, diagramType]);

  // Aggregate common text/shape styles across selection for fast UI reflection
  const selectionStyles = useMemo<SelectionStylesAggregate>(() => {
    const ids = Array.from(selectedIds);
    const initial: SelectionStylesAggregate = { isMixed: {} } as any;
    if (ids.length === 0) return { isMixed: {} } as SelectionStylesAggregate;
    const getNode = (id: string) => nodes.find(n => n.id === id);
    const values: Array<{ 
      fontFamily?: string; fontSize?: number; fontWeight?: string; fontStyle?: string; 
      textAlign?: 'left'|'center'|'right'|'justify'; verticalAlign?: 'top'|'middle'|'bottom'; 
      textColor?: string; fillColor?: string; strokeColor?: string; strokeWidth?: number; dash?: number[]; dashEnabled?: boolean; opacity?: number; textDecoration?: string;
      edgeColor?: string; edgeDashed?: boolean; edgeEndArrow?: 'none'|'triangle'|'circle'|'diamond'|'bar'; edgeRoutingType?: 'straight'|'orthogonal'|'orthogonalRounded'|'bezier'; edgeLabel?: string; edgeLabelOffset?: number;
    }>= ids.map(id => {
      const n: any = getNode(id);
      const v: any = {};
      // Prefer attrs (live style updates) over legacy top-level fields so tools reflect changes immediately
      v.fontFamily = n?.attrs?.fontFamily ?? n?.fontFamily;
      v.fontSize = n?.attrs?.fontSize ?? n?.fontSize;
      v.fontWeight = n?.attrs?.fontWeight;
      v.fontStyle = n?.attrs?.fontStyle;
      v.textAlign = n?.attrs?.textAlign;
      v.verticalAlign = n?.attrs?.verticalAlign;
      v.textColor = n?.attrs?.textColor;
      v.fillColor = n?.fillColor ?? n?.attrs?.fillColor;
      v.strokeColor = n?.strokeColor ?? n?.attrs?.strokeColor;
      v.strokeWidth = n?.strokeWidth ?? n?.attrs?.strokeWidth;
      v.dash = n?.attrs?.dash;
      v.dashEnabled = n?.attrs?.dashEnabled;
      v.opacity = n?.attrs?.opacity ?? n?.opacity;
      v.textDecoration = n?.attrs?.textDecoration;
      // Edge values if selected id is an edge
      const e: any = edges.find(ed => ed.id === id);
      if (e) {
        v.edgeColor = e?.attrs?.color;
        v.edgeDashed = e?.attrs?.dashed;
        v.edgeEndArrow = e?.attrs?.endArrow?.type;
        v.edgeRoutingType = e?.attrs?.routingType;
        v.edgeLabel = e?.attrs?.label;
        v.edgeLabelOffset = e?.attrs?.labelOffset;
      }
      return v;
    });
    const pick = <K extends keyof SelectionStylesAggregate>(key: K) => {
      const uniq = Array.from(new Set(values.map(v => (v as any)[key]).filter(x => x !== undefined)));
      if (uniq.length === 1) return uniq[0] as any;
      (initial.isMixed as any)[key] = true;
      return uniq[0] as any; // primary value for display when mixed
    };
    const defaultFontSize = diagramType === 'flowchart' ? 12 : 16;
    return {
      fontFamily: pick('fontFamily'),
      fontSize: pick('fontSize') ?? defaultFontSize,
      fontWeight: pick('fontWeight'),
      fontStyle: pick('fontStyle'),
      textAlign: pick('textAlign'),
      verticalAlign: pick('verticalAlign'),
      textColor: pick('textColor'),
      fillColor: pick('fillColor'),
      strokeColor: pick('strokeColor'),
      strokeWidth: pick('strokeWidth'),
      dash: pick('dash'),
      dashEnabled: pick('dashEnabled'),
      opacity: pick('opacity'),
      textDecoration: pick('textDecoration'),
      edgeColor: pick('edgeColor'),
      edgeDashed: pick('edgeDashed'),
      edgeEndArrow: pick('edgeEndArrow'),
      edgeRoutingType: pick('edgeRoutingType'),
      edgeLabel: pick('edgeLabel'),
      edgeLabelOffset: pick('edgeLabelOffset'),
      isMixed: initial.isMixed,
    } as SelectionStylesAggregate;
  }, [selectedIds, nodes, edges, diagramType]);

  // Aggregate common data fields for data panel tools (single/mixed awareness)
  const selectionData = useMemo<SelectionDataAggregate>(() => {
    const ids = Array.from(selectedIds);
    const init: SelectionDataAggregate = { isMixed: {} } as any;
    if (ids.length === 0) return { isMixed: {} } as SelectionDataAggregate;
    const getNode = (id: string) => nodes.find(n => n.id === id) as any;
    const vals = ids.map((id) => {
      const n = getNode(id) || {};
      return {
        name: n.name,
        sex: n.sex,
        deceased: n.deceased,
        birthDate: n.birthDate,
        deathDate: n.deathDate,
        location: n.location,
        culturalHeritageTagsCount: Array.isArray(n.culturalHeritageTags) ? n.culturalHeritageTags.length : undefined,
        medicalConditionTagsCount: Array.isArray(n.medicalConditionTags) ? n.medicalConditionTags.length : undefined,
      } as SelectionDataAggregate;
    });
    const pick = <K extends keyof SelectionDataAggregate>(key: K) => {
      const uniq = Array.from(new Set(vals.map(v => (v as any)[key]).filter(x => x !== undefined)));
      if (uniq.length === 1) return uniq[0] as any;
      (init.isMixed as any)[key] = true;
      return uniq[0] as any;
    };
    return {
      name: pick('name'),
      sex: pick('sex'),
      deceased: pick('deceased'),
      birthDate: pick('birthDate'),
      deathDate: pick('deathDate'),
      location: pick('location'),
      culturalHeritageTagsCount: pick('culturalHeritageTagsCount'),
      medicalConditionTagsCount: pick('medicalConditionTagsCount'),
      isMixed: init.isMixed,
    } as SelectionDataAggregate;
  }, [selectedIds, nodes]);

  // Create the tool context
  const toolContext = useMemo<IToolContext>(() => ({
    selection: selectedIds,
    shapes: shapesMap,
    connectors: connectorsMap,
    frame: currentFrame,
    diagramType,
    stage: stageRef.current!,
    layer: layerRef.current!,
    eventBus,
    selectionSummary,
    selectionStyles,
    selectionData,
    canWrite: true, // Default to true for editors (this should be passed from parent)
    getSelectedShapes,
    getSelectedConnectors,
    getShapesInFrame
  }), [
    selectedIds,
    shapesMap,
    connectorsMap,
    currentFrame,
    diagramType,
    stageRef.current,
    layerRef.current,
    eventBus,
    selectionSummary,
    selectionStyles,
    selectionData
  ]);

  return toolContext;
};
